import { UserIdentifierType } from '@fuul/sdk';
import { PublicKey } from '@solana/web3.js';
import { isAddress } from 'viem';

import { isValidStellarEd25519PublicKey } from './stellar-strkey';

enum UserIdentifierFormat {
  Address = 'address',
  Email = 'email',
}

export abstract class Identifier {
  constructor(
    readonly userIdentifier: string,
    readonly identifierType: UserIdentifierType | string,
  ) {
    if (!identifierType) {
      throw new Error('Invalid empty blockchain type');
    }
  }

  public abstract isValid(): boolean;

  abstract toString(): string;

  abstract getFormat(): UserIdentifierFormat;

  public equals(other: Identifier): boolean {
    if (!other) {
      return false;
    }
    return this.toString() === other.toString();
  }

  static create(userIdentifier: string, identifierType: UserIdentifierType | string): Identifier {
    switch (identifierType) {
      case UserIdentifierType.EvmAddress:
        return new EvmAddressIdentifier(userIdentifier);
      case UserIdentifierType.SolanaAddress:
        return new SolanaAddressIdentifier(userIdentifier);
      case 'sui_address':
        return new SuiAddressIdentifier(userIdentifier);
      case 'xrpl_address':
        return new XRPLAddressIdentifier(userIdentifier);
      case UserIdentifierType.StellarAddress:
        return new StellarAddressIdentifier(userIdentifier);
      case UserIdentifierType.Email:
        return new EmailUserIdentifier(userIdentifier);
      default:
        throw new Error(`Unsupported user identifier identifier: ${identifierType}`);
    }
  }

  static fromString(userIdentifier: string): Identifier | null {
    const userIdentifierType = Identifier.getUserIdentifierType(userIdentifier);
    if (!userIdentifierType) {
      return null;
    }
    return Identifier.create(userIdentifier, userIdentifierType);
  }

  static isAddress(address: string): boolean {
    if (!address) {
      return false;
    }
    return (
      new EvmAddressIdentifier(address).isValid() ||
      new SolanaAddressIdentifier(address).isValid() ||
      new SuiAddressIdentifier(address).isValid() ||
      new XRPLAddressIdentifier(address).isValid() ||
      new StellarAddressIdentifier(address).isValid()
    );
  }

  static getUserIdentifierType(address: string): UserIdentifierType | string | null {
    if (new EvmAddressIdentifier(address).isValid()) {
      return UserIdentifierType.EvmAddress;
    }
    if (new SolanaAddressIdentifier(address).isValid()) {
      return UserIdentifierType.SolanaAddress;
    }
    if (new SuiAddressIdentifier(address).isValid()) {
      return 'sui_address';
    }
    if (new XRPLAddressIdentifier(address).isValid()) {
      return 'xrpl_address';
    }
    if (new StellarAddressIdentifier(address).isValid()) {
      return UserIdentifierType.StellarAddress;
    }
    if (new EmailUserIdentifier(address).isValid()) {
      return UserIdentifierType.Email;
    }
    return null;
  }
}

export class EvmAddressIdentifier extends Identifier {
  constructor(address: string) {
    super(address, UserIdentifierType.EvmAddress);
  }

  isValid(): boolean {
    return !!this.userIdentifier && isAddress(this.userIdentifier);
  }

  toString(): string {
    return this.userIdentifier.toLowerCase();
  }

  getFormat(): UserIdentifierFormat {
    return UserIdentifierFormat.Address;
  }
}

export class SolanaAddressIdentifier extends Identifier {
  constructor(address: string) {
    super(address, UserIdentifierType.SolanaAddress);
  }

  isValid(): boolean {
    if (!this.userIdentifier) {
      return false;
    }
    try {
      new PublicKey(this.userIdentifier);
      return true;
    } catch {
      return false;
    }
  }

  toString(): string {
    return this.userIdentifier;
  }

  getFormat(): UserIdentifierFormat {
    return UserIdentifierFormat.Address;
  }
}

export class SuiAddressIdentifier extends Identifier {
  constructor(address: string) {
    super(address, 'sui_address');
  }

  isValid(): boolean {
    if (!this.userIdentifier) return false;
    return /^0x[a-fA-F0-9]{64}$/.test(this.userIdentifier);
  }

  toString(): string {
    return this.userIdentifier.toLowerCase();
  }

  getFormat(): UserIdentifierFormat {
    return UserIdentifierFormat.Address;
  }
}

export class XRPLAddressIdentifier extends Identifier {
  constructor(address: string) {
    super(address, 'xrpl_address');
  }

  isValid(): boolean {
    if (!this.userIdentifier) return false;
    return /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(this.userIdentifier);
  }

  toString(): string {
    return this.userIdentifier;
  }

  getFormat(): UserIdentifierFormat {
    return UserIdentifierFormat.Address;
  }
}

export class StellarAddressIdentifier extends Identifier {
  constructor(address: string) {
    super(address, UserIdentifierType.StellarAddress);
  }

  isValid(): boolean {
    // Validates the base32 + CRC16 checksum (canonical). Mirrors the
    // Solana `new PublicKey()` decode-validation above — NOT the Sui/XRPL regex.
    return !!this.userIdentifier && isValidStellarEd25519PublicKey(this.userIdentifier);
  }

  toString(): string {
    // RAW — StrKey base32 is case-sensitive. Do NOT .toLowerCase() (mirrors
    // XRPL/Solana toString(), NOT Sui). Lowercasing corrupts the address and
    // desyncs equals() from normalizeIdentifier's case-preserving default.
    return this.userIdentifier;
  }

  getFormat(): UserIdentifierFormat {
    return UserIdentifierFormat.Address;
  }
}

export class EmailUserIdentifier extends Identifier {
  constructor(address: string) {
    super(address, UserIdentifierType.Email);
  }

  isValid(): boolean {
    return !!this.userIdentifier && typeof this.userIdentifier === 'string' && this.userIdentifier.includes('@'); // TODO implement proper email validation
  }

  toString(): string {
    return this.userIdentifier;
  }

  getFormat(): UserIdentifierFormat {
    return UserIdentifierFormat.Email;
  }
}
